import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import { StatBox, StyledBox } from '~Scenes/EndOfDay/styles';
import { EnterEODButton, PrintZReportButton } from '../SalesInfo';
import { useStorage } from '~Components/Storage';
import { useReportsUniverse } from '~Components/ReportsUniverse';
import { useTableUniverse } from '~Components/TableUniverse';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import Icon from '~ComponentsImproved/Icon';
import CheckIconSrc from '~Assets/icons/check.svg';
import { DateTime } from 'luxon';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  min-height: calc(100vh - 64px);
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  margin-top: 4px;
  box-sizing: border-box;
  padding: 16px;
`;

interface Props {
  loading: boolean;
  cashAmountEntered: boolean;
  enteredCashOut: number;
  printingReport: boolean;
  handleEnterEOD: () => void;
  handleFinish: () => void;
}

const EndOfDayHandy: FC<Props> = ({
  loading,
  cashAmountEntered,
  enteredCashOut,
  printingReport,
  handleEnterEOD,
  handleFinish,
}) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const currency = useCurrency();

  const reportsUniverse = useReportsUniverse();
  const tableUniverse = useTableUniverse();

  // Get the configured payment methods
  const paymentMethods = useSelector((state) =>
    state.POSPaymentMethods.sort((a, b) => a.title.localeCompare(b.title)),
  );
  const country = useSelector((state) => state.country);
  const hasIndividualEOD = useSelector((state) => state.hasIndividualEOD);

  const isCashActivated = !!paymentMethods.find((card) => card.id === 'CASH');
  const shouldNotEnterCash = hasIndividualEOD && storage.disableCash;

  const disableEODBasedOnOpenTables =
    country === 'NO'
      ? tableUniverse?.cheques
          // Filter out cheques created in the POS mode in handy
          .filter((c) => !c.tableId?.startsWith('POS_'))
          .filter((c) => c.tableId !== PARKED_ORDER_TABLE_ID) // Filter out cheques that is parked orders
          .some((c) => c.tableId)
      : false;

  const isZreportActive =
    (isCashActivated && cashAmountEntered) ||
    (isCashActivated && cashAmountEntered) ||
    !isCashActivated ||
    !isCashActivated;

  const cashEntryDisabledReasons = useMemo(() => {
    return {
      dayNotStarted: {
        condition: !reportsUniverse.dayStarted,
        message: i18n('EOD.EnterCashError.DayNotStarted'),
      },
      openTables: {
        condition: disableEODBasedOnOpenTables,
        message: i18n('EOD.EnterCashError.OpenTables'),
      },
      deviceSpecificDisable: {
        condition: shouldNotEnterCash,
        message: i18n('EOD.EnterCashError.DeviceSpecific'),
      },
    };
  }, [reportsUniverse.dayStarted, disableEODBasedOnOpenTables, shouldNotEnterCash, i18n]);

  const enterEODButton = useButton({
    onClick: () => handleEnterEOD(),
    disabled: !reportsUniverse.dayStarted || disableEODBasedOnOpenTables || shouldNotEnterCash,
  });

  const printZReportButton = useButton({
    onClick: () => handleFinish(),
    disabled: !isZreportActive || printingReport,
  });

  const todaysDate = DateTime.now().toLocaleString({
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const todaysData = [
    { name: i18n('EOD.GrossSales'), amount: reportsUniverse.totalSales },
    {
      name: i18n('EOD.NetSales'),
      amount: reportsUniverse.totalSales - reportsUniverse.vatTotalAmount,
    },
    { name: i18n('EOD.Tips'), amount: reportsUniverse.totalTips },
    { name: i18n('EOD.Discount'), amount: reportsUniverse.discountsTotal },
  ];

  if (loading) {
    return (
      <Box
        style={{ height: '50vh' }}
        display="flex"
        alignItems="center"
        flexDirection="column"
        gap="small"
        justifyContent="center">
        <Loader />
        <Paragraph level={4} color="var(--colorWhite)">
          {i18n('EOD.Loading')}
        </Paragraph>
      </Box>
    );
  }

  return (
    <Wrapper>
      <ScrollWrapper>
        <Heading bottom={16} level={2} color="var(--colorWhite)">
          {i18n('EOD.EndOfDay')}
        </Heading>

        <StyledBox
          background="var(--colorBlueGray1100)"
          padding="small"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap="medium"
          marginBottom="small">
          <Paragraph level={4} color="var(--colorWhite)">
            {i18n('EOD.EndOfDay.Procedure.Subtitle')}
          </Paragraph>

          {isCashActivated && (
            <Box display="flex" flexDirection="column" gap="small">
              <EnterEODButton display="flex" alignItems="center" gap="xs" {...enterEODButton}>
                {i18n('EOD.EnterEODBalance')}
                {(cashAmountEntered || shouldNotEnterCash) && (
                  <Icon src={CheckIconSrc} color="var(--colorWhite)" className="icon check" />
                )}
              </EnterEODButton>
              {Object.entries(cashEntryDisabledReasons).map(([key, { condition, message }]) => {
                if (condition) {
                  return (
                    <Paragraph key={key} level={4} color="var(--colorUIOrange)">
                      • {message}
                    </Paragraph>
                  );
                }
                return null;
              })}
            </Box>
          )}

          <PrintZReportButton {...printZReportButton}>
            {i18n('EOD.PrintZReport')}
          </PrintZReportButton>
        </StyledBox>

        <StyledBox
          background="var(--colorBlueGray1100)"
          padding="small"
          display="flex"
          flexDirection="column"
          gap="xs">
          <Heading level={4} color="var(--colorWhite)">
            {todaysDate}
          </Heading>
          <Box>
            {todaysData.map(({ name, amount }) => (
              <StatBox
                borderColor="var(--colorBlueGray900)"
                display="flex"
                justifyContent="space-between"
                key={name}>
                <Paragraph level={4} color="var(--colorWhite)">
                  {name}
                </Paragraph>
                <Heading level={5} color="var(--colorWhite)">
                  {amount ? currency.format(amount, true) : '-'}
                </Heading>
              </StatBox>
            ))}
          </Box>

          {isZreportActive && (
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {i18n('EOD.EndOfDay')}
                </Paragraph>
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {currency.format(enteredCashOut, true)}
                </Paragraph>
              </Box>
            </Box>
          )}
        </StyledBox>
      </ScrollWrapper>
    </Wrapper>
  );
};

export default EndOfDayHandy;
